import { createSignal, createEffect, onCleanup } from 'solid-js'

interface CountdownProps {
  completion: boolean
}

export default function Component({ completion }: CountdownProps) {
  const [progress, setProgress] = createSignal(0)

  createEffect(() => {
    const totalDuration = 14000 // 14 seconds in milliseconds
    const fastPhaseEnd = 0.75 // Fast phase ends at 75%
    const slowPhaseEnd = 0.94 // Slow phase ends at 94%
    const verySlowPhaseEnd = 0.99 // Very slow phase ends at 99%

    const fastPhaseDuration = fastPhaseEnd * totalDuration * 0.5 // Faster fast phase
    const slowPhaseDuration = (slowPhaseEnd - fastPhaseEnd) * totalDuration // Slow phase
    const verySlowPhaseDuration = 5000 // 5 seconds for the last 5%

    const startTime = Date.now()

    const timer = setInterval(() => {
      const elapsedTime = Date.now() - startTime

      if (elapsedTime < fastPhaseDuration) {
        // Fast phase (0-75%)
        setProgress((elapsedTime / fastPhaseDuration) * 75)
      } else if (elapsedTime < fastPhaseDuration + slowPhaseDuration) {
        // Slow phase (75-94%)
        const slowPhaseElapsed = elapsedTime - fastPhaseDuration
        const slowProgress = (slowPhaseElapsed / slowPhaseDuration) * 19
        setProgress(75 + slowProgress)
      } else if (elapsedTime < fastPhaseDuration + slowPhaseDuration + verySlowPhaseDuration) {
        // Very slow phase (94-99%)
        const verySlowPhaseElapsed = elapsedTime - (fastPhaseDuration + slowPhaseDuration)
        const verySlowProgress = Math.floor(verySlowPhaseElapsed / 1000)
        setProgress(94 + verySlowProgress)
      } else {
        // Wait at 99%
        setProgress(99)
        clearInterval(timer)
      }
    }, 16) // Update roughly 60 times per second for smooth animation

    onCleanup(() => clearInterval(timer))
  })

  createEffect(() => {
    if (completion && progress() >= 99) {
      setProgress(100)
    }
  })

  return (
    <div class="mx-auto w-full max-w-md space-y-4">
      <ProgressBar value={progress()} />
      <p class="text-center text-lg font-semibold">{progress().toFixed(0)}%</p>
    </div>
  )
}

const ProgressBar = (props: { value: number }) => {
  return (
    <>
      <div class="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700">
        <div class="h-2.5 rounded-full bg-cosplay-purple" style={`width: ${props.value}%`}></div>
      </div>
    </>
  )
}
